import React from 'react';

import { CardListSkeleton } from '@eika-kortbetjening/card-list-panel';
import { AgeGroupV2, RegionBlockFormV3 } from '@eika-kortbetjening/region-block-form';
import { ErrorCard } from '@eika/card';

import piwik from '@/piwik/piwik';
import { useSetCardRules } from '@/react-query/hooks/mutation/use-set-card-rules';
import { useCard } from '@/react-query/hooks/query/use-card';
import { useCardRules } from '@/react-query/hooks/query/use-card-rules';

import { useSession } from '@components/app';
import { useIsHighFraudRisk } from '@hooks/use-is-high-risk';
import useText from '@hooks/use-text';
import { isUnder13, isUnder18 } from '@utils/date-utils';

import './CardRules.scss';

const CardRules = () => {
    const text = useText();
    const { data: card } = useCard();
    const {
        data: cardRule,
        isPending,
        isError: failedFetchingCardControl,
        isFetching: isFetchingCardControl,
        isSuccess: hasFetchedCardControl,
    } = useCardRules();
    const {
        mutate,
        isPending: isPostingCardControl,
        isError: failedPostingCardControl,
        isSuccess: hasPostedCardControl,
    } = useSetCardRules();
    const isHighFraudRisk = useIsHighFraudRisk();
    const session = useSession();

    const anyError = failedPostingCardControl || failedFetchingCardControl;

    if (typeof card === 'undefined' || isPending) {
        return <CardListSkeleton />;
    }

    if (typeof cardRule === 'undefined' || !session) {
        return <CardListSkeleton />;
    }

    if (anyError) {
        return <ErrorCard title={text.regionBlockFetchError} />;
    }

    const handleEcommerceChange = (value: boolean) => {
        mutate({ cardType: card.type, cardId: card.id, cardRules: { ...cardRule, ecommerce: value } });
    };

    const handleRegionRuleChange = (regionId: string, enabled: boolean) => {
        const [, scandinavia, europe] = cardRule.regions;

        const updatedRegions = cardRule.regions.map((region) => {
            if (region.region.id === regionId) {
                return { ...region, enabled };
            }

            if (regionId === europe?.region.id && enabled) {
                if (region.region.id === scandinavia?.region.id) {
                    return { ...region, enabled: true };
                }
            }

            return region;
        });

        mutate({ cardType: card.type, cardId: card.id, cardRules: { ...cardRule, regions: updatedRegions } });
    };

    const userAgeGroup = () => {
        if (isUnder13(session.user!.birthDate)) {
            return AgeGroupV2.CHILD;
        }
        if (isUnder18(session.user!.birthDate)) {
            return AgeGroupV2.TEENAGER;
        }
        return AgeGroupV2.ADULT;
    };

    return (
        <RegionBlockFormV3
            accessInfo={{
                userAgeGroup: userAgeGroup(),
                userIsHighFraudRisk: isHighFraudRisk,
            }}
            cardControlLoader={{
                isFetchingCardControl,
                hasFetchedCardControl,
                failedFetchingCardControl,
                isPostingCardControl,
                hasPostedCardControl,
                failedPostingCardControl,
            }}
            cardRule={cardRule}
            handleEcommerceChange={handleEcommerceChange}
            handleRegionRuleChange={handleRegionRuleChange}
            piwik={piwik}
            sitecoreDictionary={text}
        />
    );
};

export default CardRules;
